
import React, { useEffect, useContext } from 'react';
import { useProgram } from '../ProgramContext';
import { useNavigate } from 'react-router-dom';
import LanguageContext from '../LanguageContext';
import AuthContext from '../AuthContext';


function Welcome() {
  const { language, selectedLanguage } = useContext(LanguageContext);
  const { setProgram } = useProgram();
  const { isAuthenticated, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const locale = selectedLanguage === 'fr' || language === 'fr' ? 'fr' : 'en';
  const isFrench = locale === 'fr';
  const contentLang = isFrench ? 'fr' : 'en';
  const showTestingNotice = false;

  const isetCopy = {
    en: {
      skip: 'Skip to main content',
      programOption: 'ISET Program',
      title: 'Welcome to the NWAC ISET portal',
      introLead: 'The Indigenous Skills and Employment Training (ISET) program supports Indigenous women in building skills, pursuing education, and strengthening their path toward meaningful employment.',
      eligibilityHeading: 'You may be able to receive support through the ISET program if you:',
      eligibilityBullets: [
        'Identify as an Indigenous woman in all their diversities, including First Nations, Inuit, or Metis',
        'Are a Canadian citizen',
        'Are unemployed, underemployed, or at risk of becoming unemployed at the time you apply for supports',
        'Are enrolled in a publicly funded educational institution. Programs delivered through private colleges are not eligible for funding under the NWAC ISET Program.'
      ],
      eligibilityNotes: [
        'There are additional requirements that need to be met.',
        'Every person’s journey is different. If you are unsure whether this program is right for you, you are welcome to apply or reach out for guidance.',
        'Our team is here to support you along the way.'
      ],
      processHeading: 'How to Apply',
      processLead: 'You can save your application and return later.',
      processSteps: [
        'Create an account or sign in to your existing account.',
        'Complete the application form at a pace that works for you.',
        'Upload documents as prompted while completing your application.',
        'Review your information and submit your application when you are ready.'
      ],
      supportHeading: 'Support While You Apply',
      supportBody: 'If you have questions or need assistance, support is available.',
      supportBullets: [
        'Contact your NWAC ISET Case Manager (if one has already been assigned)',
        'Use Help for general portal questions, or secure Messages once you have an account.',
        'Send an email to iset@nwac.ca and someone will respond'
      ],
      moreInfoLead: 'Your information is kept confidential and is only used to review your eligibility and provide support through the ISET program.',
      programLinkLabel: 'ISET Program',
      startNow: 'Start your application'
    },
    fr: {
      skip: 'Passer au contenu principal',
      programOption: 'Programme ISET',
      title: 'Bienvenue dans le portail ISET de l’AFAC',
      introLead: 'Le programme de Formation pour les compétences et l’emploi destiné aux Autochtones (ISET) soutient les femmes autochtones dans le développement de leurs compétences, la poursuite de leurs études et le renforcement de leur parcours vers un emploi valorisant.',
      eligibilityHeading: 'Vous pourriez être admissible si vous :',
      eligibilityBullets: [
        'vous identifiez comme femme autochtone, notamment des Premières Nations, Inuit ou Métisse',
        'êtes citoyenne canadienne',
        'êtes sans emploi, sous-employée ou à risque de perdre votre emploi',
        'êtes inscrite dans un établissement d’enseignement financé par des fonds publics. Les programmes offerts par des collèges privés ne sont pas admissibles au financement dans le cadre du programme ISET de l’AFAC.'
      ],
      eligibilityNotes: [
        'Des exigences supplémentaires doivent être respectées.',
        'Chaque parcours est différent. Si vous ne savez pas si ce programme vous convient, vous pouvez quand même présenter une demande ou communiquer avec nous pour obtenir des conseils.',
        'Notre équipe est là pour vous accompagner tout au long du processus.'
      ],
      processHeading: 'Comment présenter une demande',
      processLead: 'Vous pouvez enregistrer votre demande et y revenir plus tard.',
      processSteps: [
        'Créez un compte ou connectez-vous à votre compte existant.',
        'Remplissez le formulaire de demande au rythme qui vous convient.',
        'Téléversez les documents demandés pendant que vous remplissez votre demande.',
        'Vérifiez vos renseignements et envoyez votre demande lorsque vous êtes prête.'
      ],
      supportHeading: 'Soutien pendant votre demande',
      supportBody: "Si vous avez des questions ou avez besoin d’aide, du soutien est disponible.",
      supportBullets: [
        'Communiquez avec votre gestionnaire de cas ISET de l’AFAC (si une personne vous a déjà été assignée)',
        'Utilisez Aide pour les questions générales sur le portail, ou les Messages sécurisés une fois votre compte créé.',
        'Envoyez un courriel à iset@nwac.ca et quelqu’un vous répondra'
      ],
      moreInfoLead: 'Vos renseignements demeurent confidentiels et sont utilisés uniquement pour évaluer votre admissibilité et vous offrir du soutien dans le cadre du programme ISET.',
      programLinkLabel: 'Programme ISET',
      startNow: 'Commencer votre demande'
    }
  };
  const isetText = isetCopy[locale] || isetCopy.en;

  const notificationContent = isFrench
    ? {
        title: 'Avis de tests',
        heading: 'Avis de tests - lundi 5 janvier 2026',
        patchNotesLabel: 'Notes de mise à jour :',
        patchNotes: [
          { text: 'Le portail public masque maintenant le bouton « Commencer une nouvelle demande » lorsqu’une demande existante est en cours de traitement.' },
          { text: 'Le tableau de bord des utilisateurs affiche maintenant une carte différente pour les participants ayant des activités du plan en cours.' },
          { text: 'Les demandes disparaissent maintenant une fois fermées.' }
        ],
        knownIssuesLabel: 'Problèmes connus :',
        knownIssues: [
          { text: 'La connexion reste sensible à la casse des courriels - et le restera pour le moment.' },
          { text: 'Si les libellés des cases à cocher se chevauchent encore, faites un rafraîchissement forcé de votre navigateur (CTRL+F5). Le correctif est en place, mais d’anciennes feuilles de style peuvent encore être mises en cache.' }
        ]
      }
    : {
        title: 'Testing notice',
        heading: 'Testing notice - Monday 5th January 2026',
        patchNotesLabel: 'Patch Notes:',
        patchNotes: [
          { text: 'The public portal now hides the "Start New Application" button when an existing application is being processed.' },
          { text: 'The user dashboard now shows a different card for participants with current plan activities.' },
          { text: 'Applications now disappear once they are closed.' }
        ],
        knownIssuesLabel: 'Known Issues:',
        knownIssues: [
          { text: 'login emil remains case sensistive - and will do for the time being' },
          { text: 'If checkbox labels still overlap their boxes, hard refresh your browser (CTRL+F5). This has been fixed, but sometimes old stylesheets are being cached.' }
        ]
      };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setProgram('ISET');
  }, [setProgram]);

  return (
    <>
      {/* WCAG 2.4.1: Skip to main content link */}
      <a href="#main-content" className="govuk-skip-link">
        {isetText.skip}
      </a>
      <div>
        <main
          className="govuk-main-wrapper welcome-page"
          id="main-content"
          role="main"
          tabIndex="-1"
        >
          <div className="govuk-width-container welcome-page__content">
            {showTestingNotice && (
              <div
                className="govuk-notification-banner govuk-notification-banner--info"
                role="region"
                aria-labelledby="tester-banner-title"
                data-module="govuk-notification-banner"
              >
                <div className="govuk-notification-banner__header">
                  <h2 className="govuk-notification-banner__title" id="tester-banner-title">
                    {notificationContent.title}
                  </h2>
                </div>
                <div className="govuk-notification-banner__content" lang={isFrench ? 'fr' : 'en'}>
                  <p className="govuk-notification-banner__heading">
                    {notificationContent.heading}
                  </p>
                  <p className="govuk-!-margin-bottom-1 govuk-body">{notificationContent.patchNotesLabel}</p>
                  <ul className="govuk-list govuk-list--bullet govuk-!-margin-top-1">
                    {notificationContent.patchNotes.map(note => (
                      <li key={note.text}>
                        {note.emphasis ? <em>{note.text}</em> : note.text}
                      </li>
                    ))}
                  </ul>
                  <p className="govuk-!-margin-bottom-1 govuk-body">{notificationContent.knownIssuesLabel}</p>
                  <ul className="govuk-list govuk-list--bullet govuk-!-margin-top-1">
                    {notificationContent.knownIssues.map(issue => (
                      <li key={issue.text}>{issue.text}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {/* Introduction */}
            <h1 className="govuk-heading-l" lang={contentLang}>{isetText.title}</h1>
            <p className="govuk-body" lang={contentLang}>
              <>
                <b>{isetText.introLead}</b>
              </>
            </p>

            {/* Eligibility Criteria */}
            <h2 className="govuk-heading-m" lang={contentLang}>{isetText.eligibilityHeading}</h2>
            <>
              <ul className="govuk-list govuk-list--bullet" lang={contentLang}>
                {isetText.eligibilityBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {Array.isArray(isetText.eligibilityNotes) && isetText.eligibilityNotes.map((item) => (
                <p key={item} className="govuk-body" lang={contentLang}>{item}</p>
              ))}
            </>

            {/* Application Process */}
            <h2 className="govuk-heading-m" lang={contentLang}>{isetText.processHeading}</h2>
            <p className="govuk-body" lang={contentLang}>{isetText.processLead}</p>
            <ol className="govuk-list govuk-list--number" lang={contentLang}>
              {isetText.processSteps.map((item) => <li key={item}>{item}</li>)}
            </ol>

            {/* Support and Assistance */}
            <h2 className="govuk-heading-m" lang={contentLang}>{isetText.supportHeading}</h2>
            <p className="govuk-body" lang={contentLang}>{isetText.supportBody}</p>
            {Array.isArray(isetText.supportBullets) && (
              <>
                <ul className="govuk-list govuk-list--bullet" lang={contentLang}>
                  {isetText.supportBullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </>
            )}
            <p className="govuk-body" lang={contentLang}>{isetText.moreInfoLead}</p>

            {/* Start button */}
            <div className="govuk-button-group">
              <button
                className="govuk-button govuk-button--start"
                lang={contentLang}
                disabled={loading}
                onClick={() => {
                  if (loading) return;
                  if (isAuthenticated) {
                    try { localStorage.setItem('iset.flow', 'dynamic'); } catch (_) {}
                    navigate('/dashboard');
                  } else {
                    navigate('/login', { state: { program: 'ISET' } });
                  }
                }}
              >
                {isetText.startNow}
              </button>
              
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

// WCAG: Ensure <title> and <html lang> are set in public/index.html
// WCAG: Test color contrast, focus visibility, and reflow with browser tools

export default Welcome;
